import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { distinctUntilChanged, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  DashboardToolOption,
  ProjectDashboardHeaderComponent
} from './components/project-dashboard-header/project-dashboard-header.component';
import { ProjectDashboardDockComponent } from './components/project-dashboard-dock/project-dashboard-dock.component';
import { ProjectDashboardStepperComponent } from './components/project-dashboard-stepper/project-dashboard-stepper.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { ProjectPreviewService } from '../project-preview/services/project-preview.service';

@Component({
  selector: 'project-dashboard',
  standalone: true,
  templateUrl: './project-dashboard.component.html',
  styleUrls: ['./project-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    RouterModule,
    ProjectDashboardHeaderComponent,
    ProjectDashboardDockComponent,
    ProjectDashboardStepperComponent
  ]
})
export class ProjectDashboardComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly projectService = inject(ProjectAPIService);
  private readonly projectStore = inject(ProjectStoreService);
  private readonly projectPreviewService = inject(ProjectPreviewService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly refreshProject$ = new Subject<void>();

  readonly draggedToolFromHeader = signal<DashboardToolOption | null>(null);
  readonly project = signal<Project | null>(null);
  readonly currentRoute = signal<string | null>(null);
  readonly activeStep = signal<ProjectStage | null>(null);

  readonly isSummaryPage = computed(() => this.currentRoute() === 'summary');
  readonly isConfigPage = computed(() => this.currentRoute() === 'configurator');

  constructor() {
    const projectId$ = this.activatedRoute.paramMap.pipe(
      map((params) => Number(params.get('projectId'))),
      filter((projectId) => Number.isFinite(projectId) && projectId > 0),
      distinctUntilChanged()
    );

    projectId$
      .pipe(
        tap((projectId) => this.prepareProjectLoading(projectId)),
        switchMap((projectId) => this.projectService.getProject(projectId)),
        tap((project) => this.setProject(project)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.refreshProject$
      .pipe(
        withLatestFrom(projectId$),
        switchMap(([, projectId]) => this.projectService.getProject(projectId)),
        tap((project) => this.setProject(project)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.initRouterEvents();
  }

  private initRouterEvents(): void {
    this.updateRouteState(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        tap(({ url, urlAfterRedirects }) => this.updateRouteState(urlAfterRedirects || url)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private updateRouteState(currentUrl: string): void {
    const tree = this.router.parseUrl(currentUrl);
    const segments = tree.root.children['primary']?.segments ?? [];
    const paths = segments.map((segment) => segment.path);
    const stageTasksIndex = paths.indexOf('stage-tasks');
    const routeStage = stageTasksIndex > -1 ? paths[stageTasksIndex + 1] : null;

    if (routeStage && Object.values(ProjectStage).includes(routeStage as ProjectStage)) {
      this.activeStep.set(routeStage as ProjectStage);
      this.currentRoute.set('tasks');
      return;
    }

    this.activeStep.set(null);
    const lastSegment = paths.length > 0 ? paths[paths.length - 1] : '';
    this.currentRoute.set(lastSegment);
  }

  private prepareProjectLoading(projectId: number): void {
    const currentProjectId = this.project()?.id ?? null;
    if (currentProjectId === projectId) return;

    this.project.set(null);
    this.projectStore.project.set(null);
  }

  private setProject(project: Project): void {
    this.project.set(project);
    this.projectStore.project.set(project);
    this.projectStore.projectCtrl.setProject(project);
    this.projectPreviewService.project = project;
  }

  protected onToolDragStarted(item: DashboardToolOption): void {
    this.draggedToolFromHeader.set(item);
  }

  protected onToolDragEnded(): void {
    this.draggedToolFromHeader.set(null);
  }

  protected onStageChanged(stage: ProjectStage): void {
    this.activeStep.set(stage);
    this.currentRoute.set('tasks');

    void this.router.navigate(['stage-tasks', stage], {
      relativeTo: this.activatedRoute,
      queryParams: { step: stage, tool: 'tasks' },
      queryParamsHandling: 'merge'
    });

    this.refreshProject$.next();
  }

  protected onStepSelected(key: ProjectStage): void {
    this.router.navigate(['stage-tasks', key], {
      relativeTo: this.activatedRoute,
      queryParams: { step: key, tool: 'tasks' },
      queryParamsHandling: 'merge'
    });
  }

  protected ngOnDestroy(): void {
    this.projectStore.project.set(null);
    this.projectPreviewService.project = null;
  }
}
